import React from "react";

import { useDrop } from "react-dnd";

import "react-toastify/dist/ReactToastify.css";

import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Dashboard as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import TaskCard from "./TaskCard";

/***************************************
 * Item Types (used by React DnD)
 ***************************************/
const ItemTypes = {
  TASK: "task",
};

/***************************************
 * Theme Configuration
 ***************************************/
const lightThemeColors = {
  primary: "#4361ee",
  secondary: "#3a0ca3",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#f8f9fd",
  card: "#ffffff",
  text: "#2d3748",
  textSecondary: "#718096",
  columnHeader: "#ebf3ff",
  columnBackground: "#ffffff",
  columnBackgroundHover: "#f7faff",
  taskBacklog: "#ffffff",
  taskInProgress: "#fff8f0",
  taskDone: "#f0fff4",
  taskBorder: "#e2e8f0",
  taskShadow: "0 4px 6px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.08)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.08)",
  headerBackground: "#ffffff",
  headerShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const darkThemeColors = {
  primary: "#4361ee",
  secondary: "#7b5cff",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#1a202c",
  card: "#2d3748",
  text: "#f7fafc",
  textSecondary: "#a0aec0",
  columnHeader: "#2d3748",
  columnBackground: "#2d3748",
  columnBackgroundHover: "#3a4556",
  taskBacklog: "#2d3748",
  taskInProgress: "#383245",
  taskDone: "#304035",
  taskBorder: "#4a5568",
  taskShadow: "0 4px 6px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.4)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)",
  headerBackground: "#2d3748",
  headerShadow: "0 1px 3px rgba(0,0,0,0.3)",
};

/***************************************
 * Column Component - Redesigned
 ***************************************/
const Column = ({
  column,
  tasks,
  onOpenAnr,
  moveTask,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onArchiveTask,
  onStartTimer,
  onStopTimer,
  onResetTimer,
  onShowBarcode,
  onAddComment,
  onEditComment,
  onDeleteComment,
  isDarkMode,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;
  const [{ isOver }, dropRef] = useDrop({
    accept: ItemTypes.TASK,
    drop: (item) => {
      moveTask(item.task.id, item.from, column.id);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  // Get icon and color based on column type
  let columnIcon, columnColor, columnBgColor;
  switch (column.id) {
    case "backlog":
      columnIcon = <AssignmentIcon />;
      columnColor = "#4361ee";
      columnBgColor = isDarkMode ? "rgba(67, 97, 238, 0.15)" : "#ebf3ff";
      break;
    case "inprogress":
      columnIcon = <AccessTimeIcon />;
      columnColor = "#f39c12";
      columnBgColor = isDarkMode ? "rgba(243, 156, 18, 0.15)" : "#fff8f0";
      break;
    case "done":
      columnIcon = <SaveIcon />;
      columnColor = "#2ecc71";
      columnBgColor = isDarkMode ? "rgba(46, 204, 113, 0.15)" : "#f0fff4";
      break;
    default:
      columnIcon = <DashboardIcon />;
      columnColor = themeColors.primary;
      columnBgColor = isDarkMode ? "rgba(67, 97, 238, 0.15)" : "#ebf3ff";
  }

  return (
    <Paper
      ref={dropRef}
      elevation={isOver ? 3 : isDarkMode ? 0 : 1}
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: isOver
          ? themeColors.columnBackgroundHover
          : themeColors.columnBackground,
        borderRadius: 4,
        overflow: "hidden",
        transition: "all 0.2s ease",
        height: "100%",
        border: isDarkMode ? `1px solid ${themeColors.taskBorder}` : "none",
      }}
    >
      <Box
        sx={{
          p: isCollapsed ? 1.5 : 2.5,
          bgcolor: columnBgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.03)"}`,
          flexDirection: isCollapsed ? "column" : "row",
        }}
      >
        {isCollapsed ? (
          // Collapsed view - vertical layout
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              gap: 1,
            }}
          >
            <Avatar
              sx={{
                bgcolor: isDarkMode
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(255,255,255,0.8)",
                color: columnColor,
                width: 34,
                height: 34,
              }}
            >
              {columnIcon}
            </Avatar>

            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: themeColors.text,
                fontSize: "0.7rem",
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                whiteSpace: "nowrap",
              }}
            >
              {column.title}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: themeColors.textSecondary,
                fontSize: "0.65rem",
                fontWeight: 600,
              }}
            >
              {tasks.length}
            </Typography>

            <Tooltip title="Erweitern">
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                sx={{
                  bgcolor: isDarkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.04)",
                  color: columnColor,
                  "&:hover": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.15)"
                      : "rgba(0,0,0,0.08)",
                  },
                  width: 28,
                  height: 28,
                  mt: 1,
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          // Expanded view - horizontal layout
          <>
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Avatar
                sx={{
                  bgcolor: isDarkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(255,255,255,0.8)",
                  color: columnColor,
                  width: 34,
                  height: 34,
                }}
              >
                {columnIcon}
              </Avatar>
              <Box sx={{ ml: 1.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: themeColors.text,
                    fontSize: "1.1rem",
                  }}
                >
                  {column.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: themeColors.textSecondary,
                  }}
                >
                  {tasks.length} Aufgaben
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Aufgabe hinzufügen">
                <IconButton
                  size="small"
                  onClick={() => onAddTask(column.id)}
                  sx={{
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.04)",
                    color: columnColor,
                    "&:hover": {
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.15)"
                        : "rgba(0,0,0,0.08)",
                    },
                    width: 34,
                    height: 34,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zusammenklappen">
                <IconButton
                  size="small"
                  onClick={onToggleCollapse}
                  sx={{
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.04)",
                    color: themeColors.textSecondary,
                    "&:hover": {
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.15)"
                        : "rgba(0,0,0,0.08)",
                      color: columnColor,
                    },
                    width: 34,
                    height: 34,
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>

      {!isCollapsed && (
        <Box
          sx={{
            p: 2,
            flex: 1,
            overflowY: "auto",
            minHeight: "60vh",
            backgroundColor: isDarkMode
              ? "rgba(0,0,0,0.1)"
              : "rgba(0,0,0,0.01)",
          }}
        >
          {tasks.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: themeColors.textSecondary,
                p: 4,
                borderRadius: 2,
                border: `2px dashed ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)"}`,
                minHeight: "200px",
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: "50%",
                  bgcolor: isDarkMode
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.03)",
                }}
              >
                {columnIcon}
              </Box>
              <Typography variant="body2" align="center" fontWeight={500}>
                Keine Aufgaben vorhanden
              </Typography>
              <Typography
                variant="caption"
                align="center"
                sx={{ mt: 1, px: 4 }}
              >
                Klicken Sie auf + um eine neue Aufgabe zu erstellen, oder ziehen
                Sie Aufgaben hierher.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAddTask(column.id)}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  color: columnColor,
                  borderColor: columnColor,
                  "&:hover": {
                    borderColor: columnColor,
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.05)"
                      : `${columnColor}10`,
                  },
                }}
              >
                Aufgabe hinzufügen
              </Button>
            </Box>
          ) : (
            tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                columnId={column.id}
                onOpenAnr={onOpenAnr}
                onEditTask={onEditTask}
                onDeleteTask={onDeleteTask}
                onArchiveTask={onArchiveTask}
                onStartTimer={onStartTimer}
                onStopTimer={onStopTimer}
                onResetTimer={onResetTimer}
                onShowBarcode={onShowBarcode}
                onAddComment={onAddComment}
                onEditComment={onEditComment}
                onDeleteComment={onDeleteComment}
                isDarkMode={isDarkMode}
              />
            ))
          )}
        </Box>
      )}
    </Paper>
  );
};

export default Column;
